import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
} from "@mui/material";
import SubmissionStatusModal from "../../../components/popup-modal/submitStatusModal";
import ClientAPI from "../../../api/clientApi";

const CreateSubscriptionModal = ({ isOpen, onClose, afterSubmit,submitHistory }) => {
  const [clientName, setClientName] = useState("");
  const [numIds, setNumIds] = useState("");
  const [numApps, setNumApps] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({ clientName: "" });
  const [modalOpen, setModalOpen] = useState(false); // For SubmissionStatusModal
  const [isSuccess, setIsSuccess] = useState(false); // Success status
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!isOpen) {
      // Reset fields when the modal closes
      setClientName("");
      setNumIds("");
      setNumApps("");
      setErrors({ clientName: "" });
      setErrorMessage("");
      setModalOpen(false); // Close status modal on close
    }
  }, [isOpen]);

  const validateClientName = (name) => {
    if (name.trim() === "") {
      return "Client Name is required";
    } else if (!/^[A-Za-z\s]{1,50}$/.test(name)) {
      return "Invalid Client Name";
    }
    return "";
  };
  const validateNumIds = (num) => {
    if (num.trim() === "") {
      return "Number of IDs is required";
    } else if (parseInt(num, 10) <= 0) {
      return "Number of IDs must be greater than 0";
    }
    return "";
  };

  const validateNumApps = (num) => {
    if (num.trim() === "") {
      return "Number of Applications is required";
    } else if (parseInt(num, 10) <= 0) {
      return "Number of Applications must be greater than 0";
    }
    return "";
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    onClose(); // Ensure the main dialog closes after submission.
  };

  const handleChange = (setter, fieldName) => (e) => {
    const value = e.target.value;
    setter(value);

    if (fieldName === "clientName") {
      const error = validateClientName(value);
      setErrors((prevErrors) => ({ ...prevErrors, clientName: error }));
    }
  };

  const handleFormSubmit = async () => {
    const clientNameError = validateClientName(clientName);
    const numIdsError = validateNumIds(numIds); // Validate numIds
    const numAppsError = validateNumApps(numApps); // Validate numApps
    if (clientNameError || numIdsError || numAppsError) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        clientName: clientNameError,
        numIds: numIdsError,
        numApps: numAppsError,
      }));
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    const subscriptionData = {
      clientName,
      totalNumberOfUser: numIds,
      totalNoOfApplication: numApps,
    };

    try {
      setIsSubmitting(true);

      const result = await ClientAPI.createSubscription(subscriptionData); // Create subscription API call
      if(result.statusCode==="SUCCESS"){

      setIsSuccess(true);
      setMessage(`${result.message || "Subscription created successfully"}`);
      afterSubmit();
      submitHistory();
      }
      else if (result.statusCode==="FAILED"){
        setIsSuccess(false);
        setMessage(`${result.message}`);
      }
    } catch (error) {
      // Set error message from API (error.message should have the correct API response)
      setIsSuccess(false);
      setMessage(error.message || "Failed to create subscription"); // Use dynamic API message
    } finally {
      setIsSubmitting(false);
      setModalOpen(true); // Show the modal after completion
    }
  };

  const handleClose = (event, reason) => {
    // Prevent closing the modal when clicking outside or pressing escape key
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onClose(); // Allow intentional close actions (e.g., pressing "No" button)
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} fullWidth>
        <DialogTitle>
          <Typography variant="h6" align="center" style={{ color: "red" }}>
            Create Subscription
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            fullWidth
            label="Client Name"
            variant="outlined"
            value={clientName}
            onChange={handleChange(setClientName, "clientName")}
            error={!!errors.clientName}
            helperText={errors.clientName}
            required
          />
          <TextField
            margin="normal"
            fullWidth
            label="Number of IDs"
            variant="outlined"
            value={numIds}
            onChange={(e) => {
              const value = e.target.value;
              // Allow only numeric values
              if (/^\d*$/.test(value)) {
                // Prevent 0 or negative values
                if ((value === "" || parseInt(value, 10) > 0) && value.length <= 10) {
                  setNumIds(value);
                }
              }
            }}
            onKeyDown={(e) => {
              // Prevent 'e', 'E', '+', '-', and '.' from being entered
              if (["e", "E", "+", "-", "."].includes(e.key)) {
                e.preventDefault();
              }
            }}
            inputProps={{ min: 1 }}
            required
            error={!!errors.numIds} // Show error if there is one
            helperText={errors.numIds} // Display error message
          />
          <TextField
            margin="normal"
            fullWidth
            label="Number of Applications"
            variant="outlined"
            value={numApps}
            onChange={(e) => {
              const value = e.target.value;
              // Allow only numeric values
              if (/^\d*$/.test(value)) {
                // Prevent 0 or negative values
                if ((value === "" || parseInt(value, 10) > 0) && value.length <= 10) {
                  setNumApps(value);
                }
              }
            }}
            onKeyDown={(e) => {
              // Prevent 'e', 'E', '+', '-', and '.' from being entered
              if (["e", "E", "+", "-", "."].includes(e.key)) {
                e.preventDefault();
              }
            }}
            inputProps={{ min: 1 }}
            required
            error={!!errors.numApps} // Show error if there is one
            helperText={errors.numApps} // Display error message
          />
          {errorMessage && (
            <Typography color="error" align="center">
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} style={{ color: "red" }}>
            Cancel
          </Button>
          <Button
            onClick={handleFormSubmit}
            style={{ backgroundColor: "red", color: "white" }}
            variant="contained"
            disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <SubmissionStatusModal
        open={modalOpen}
        onClose={handleCloseModal}
        isSuccess={isSuccess}
        message={message}
      />
    </>
  );
};

export default CreateSubscriptionModal;
